import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  FileText,
  ShieldCheck,
  Building2,
  MessageSquareText,
  CheckCircle2,
  ArrowLeft
} from 'lucide-react';
import TopStatsNav from '../components/TopStatsNav';

export default function Trade() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 md:p-10 overflow-hidden relative">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(33,57,112,0.10),transparent_70%)]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-2xl text-[11px] font-extrabold border border-blue-100">
                <Building2 size={16} />
                <span>تجارت فرامرزی در کریدور جنوب-شمال</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-[#1A3062] leading-tight">
                تجارت
              </h1>

              <p className="mt-4 text-sm text-gray-500 leading-8">
                این بخش برای مدیریت خدمات صادرات، واردات و ترانزیت طراحی شده است: ثبت و پیگیری درخواست‌ها، دسترسی به
                تعرفه‌ها و الزامات، و استفاده از راهنماهای کاربردی برای کاهش خطا و زمان انجام کار.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  <ArrowRight size={18} />
                  بازگشت به داشبورد
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/transport')}
                  className="h-12 px-5 rounded-2xl bg-[#213970] hover:bg-[#1A3062] text-white font-bold text-sm transition inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-900/15"
                >
                  ادامه با حمل‌ونقل
                  <ArrowLeft size={18} />
                </button>
              </div>
            </div>

            <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 shadow-sm flex items-center justify-center overflow-hidden">
              <img
                src="/imp_exp.png"
                alt="تصویر تجارت"
                className="w-full h-auto max-h-[320px] object-cover rounded-[1.75rem] shadow-lg shadow-slate-900/10 ring-1 ring-slate-900/5"
                loading="lazy"
              />
            </div>
          </div>
        </header>

        <TopStatsNav
          items={[
            {
              label: 'حمل‌ونقل چندوجهی (مسیر/هزینه/زمان)',
              value: 12,
              unit: 'مسیر فعال امروز',
              route: '/transport',
              accent: 'emerald',
              sparkValues: [2, 3, 6, 4, 7, 9]
            },
            {
              label: 'فرصت‌های سرمایه‌گذاری',
              value: 8,
              unit: 'پروژه در حال بررسی',
              route: '/investment',
              accent: 'amber',
              sparkValues: [3, 4, 4, 6, 7, 8]
            }
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h2 className="text-lg font-black text-gray-800">خدمات کلیدی تجارت</h2>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">
                  مجموعه‌ای از قابلیت‌ها برای ثبت درخواست، کنترل مدارک و کاهش رفت‌وبرگشت‌های اداری.
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
                <ShieldCheck size={22} />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Feature
                title="ثبت درخواست صادرات/واردات/ترانزیت"
                desc="ثبت سریع، پیگیری وضعیت، و دریافت اعلان تغییر مراحل."
              />
              <Feature
                title="تعرفه‌ها، الزامات و قوانین"
                desc="راهنمای قابل جستجو برای مدارک، مقررات و پرسش‌های پرتکرار."
              />
              <Feature
                title="چک‌لیست اسناد و فرم‌ها"
                desc="چک‌لیست‌های آماده برای کاهش خطا در پرونده و یکپارچه‌سازی مدارک."
              />
              <Feature
                title="شبکه شرکا و RFQ (پیشنهادی)"
                desc="یافتن شرکت‌ها/کارگزاران و ثبت درخواست قیمت (RFQ) برای مسیرهای منتخب."
              />
            </div>
          </section>

          <aside className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">مراحل پیشنهادی</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">برای شروع یک پرونده تجاری.</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700">
                <CheckCircle2 size={22} />
              </div>
            </div>

            <ol className="mt-8 space-y-4 text-sm">
              <Step n="۱" title="انتخاب نوع عملیات" desc="صادرات، واردات یا ترانزیت را مشخص کنید." />
              <Step n="۲" title="تکمیل اطلاعات کالا" desc="کد کالا، وزن، ارزش و بسته‌بندی را وارد کنید." />
              <Step n="۳" title="بارگذاری مدارک" desc="فاکتور، پکینگ و مجوزهای لازم را اضافه کنید." />
              <Step n="۴" title="ارسال برای بررسی" desc="وضعیت پرونده را تا مرحله تایید نهایی پیگیری کنید." />
            </ol>
          </aside>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">مدارک رایج</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">
                  بسته به نوع عملیات و کالا ممکن است مدارک تکمیلی نیز نیاز باشد.
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-600">
                <FileText size={22} />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
              <DocItem text="پیش‌فاکتور / فاکتور تجاری" />
              <DocItem text="Packing List / لیست عدل‌بندی" />
              <DocItem text="بارنامه و اطلاعات حمل" />
              <DocItem text="گواهی مبدا (در صورت نیاز)" />
              <DocItem text="مجوزها و استانداردها" />
              <DocItem text="بیمه‌نامه و اسناد بانکی" />
            </div>
          </section>

          <section className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
            <div className="flex items-start justify-between gap-6">
              <div className="text-right">
                <h3 className="text-lg font-black text-gray-800">راهنما و پرسش‌های پرتکرار</h3>
                <p className="text-[11px] text-gray-400 mt-2 leading-6">پاسخ‌های سریع برای شروع بهتر.</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
                <MessageSquareText size={22} />
              </div>
            </div>

            <div className="mt-8 space-y-4 text-sm text-gray-700 leading-7">
              <Faq
                q="برای شروع پرونده تجاری چه اطلاعاتی لازم است؟"
                a="نوع عملیات، مشخصات کالا (کد/وزن/ارزش)، مقصد/مبدا، و مدارک پایه مثل فاکتور و لیست عدل‌بندی."
              />
              <Faq
                q="چطور از قوانین و تعرفه‌های به‌روز مطلع شوم؟"
                a="در بخش «تعرفه‌ها / الزامات / پرسش‌های پرتکرار» می‌توانید موارد را جستجو کنید و چک‌لیست مرتبط را ببینید."
              />
              <Faq
                q="بعد از ثبت درخواست چه می‌شود؟"
                a="پرونده وارد مرحله بررسی می‌شود و در هر تغییر وضعیت، اعلان برای شما نمایش داده خواهد شد."
              />
            </div>
          </section>
        </div>

        <div className="bg-[#1A3062] rounded-[2.5rem] p-8 md:p-10 text-white shadow-xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_70%)]" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-black">آماده‌اید یک درخواست تجاری ثبت کنید؟</h3>
              <p className="text-[11px] text-blue-200 mt-3 leading-6">
                با ورود به بخش حمل‌ونقل، مسیر و هزینه را انتخاب کنید و سپس پرونده را برای بررسی ارسال نمایید.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/transport')}
              className="h-12 px-6 rounded-2xl bg-yellow-400 hover:bg-yellow-500 text-[#1A3062] font-black transition inline-flex items-center gap-2 shadow-lg"
            >
              ادامه با حمل‌ونقل
              <ArrowLeft size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ title, desc }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5 hover:shadow-sm transition">
      <div className="text-sm font-extrabold text-gray-800">{title}</div>
      <div className="text-[11px] text-gray-400 mt-2 leading-6">{desc}</div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-700 font-black text-xs flex-shrink-0">
        {n}
      </div>
      <div className="min-w-0">
        <div className="font-extrabold text-gray-800">{title}</div>
        <div className="text-[11px] text-gray-400 mt-1 leading-6">{desc}</div>
      </div>
    </li>
  );
}

function DocItem({ text }) {
  return (
    <div className="flex items-start gap-2 p-3 rounded-2xl bg-gray-50 border border-gray-100">
      <span className="w-2 h-2 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
      <span className="text-[12px] font-bold text-gray-700 leading-6">{text}</span>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5">
      <div className="text-sm font-extrabold text-gray-800">{q}</div>
      <div className="text-[11px] text-gray-500 mt-2 leading-7">{a}</div>
    </div>
  );
}

